import React, { useState, useEffect, useRef } from "react";
import "../../App.css";
import { Auth } from '../../core/auth';
import { OTPService } from '../../core/otp-service';
import { KRAYA } from '../../core/constants';
import { OAuthButtons } from '../../components/OAuthButtons';
import { AuthLogo } from '../../components/AuthLogo';
import { readOAuthSignupSession, clearOAuthSignupSession } from '../../core/oauth-flow';
import { Icon, InlineIcon } from '../../components/Icon';

function RegistrationPage({ onBack, onLogin, showToast }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", password: "", confirmPassword: "",
    companyName: "", companyType: "Private Limited",
  });
  const [verificationToken, setVerificationToken] = useState(null);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpSent, setOtpSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [verifyError, setVerifyError] = useState("");
  const [oauthSession, setOauthSession] = useState(null);
  const otpRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown(c => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [countdown]);

  useEffect(() => () => OTPService.resetRecaptcha(), []);

  useEffect(() => {
    const pending = readOAuthSignupSession();
    if (!pending?.accessToken) return;
    setOauthSession(pending);
    setForm((f) => ({
      ...f,
      name: pending.name || f.name,
      email: pending.email || f.email,
    }));
    setStep(4);
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('oauth') === '1') {
        window.history.replaceState({}, '', '/?register=1');
      }
    } catch (_) { /* ignore */ }
  }, []);

  const STEPS = ["Personal Details", "Verify Contact", "Set Password", "Company Info", "Done"];

  const nextStep = () => {
    if (step === 1) {
      if (!form.name || !form.phone || !form.email) { showToast("All fields required", "error"); return; }
      if (!form.phone.match(/^\d{10}$/)) { showToast("Enter a valid 10-digit mobile number", "error"); return; }
      if (!form.email.includes("@")) { showToast("Enter a valid email address", "error"); return; }
    }
    if (step === 3) {
      if (form.password.length < 6) { showToast("Password must be at least 6 characters", "error"); return; }
      if (form.password !== form.confirmPassword) { showToast("Passwords do not match", "error"); return; }
    }
    setStep(s => s + 1);
  };

  const sendEmailOtp = async () => {
    setVerifyError("");
    setLoading(true);
    const result = await OTPService.sendEmail("signup", form.email);
    setLoading(false);
    if (!result.success) { setVerifyError(result.error); return; }
    setOtpSent(true);
    setCountdown(30);
    setOtp(["", "", "", "", "", ""]);
    if (result.demo) showToast(`Demo OTP: ${result.otp} (see console)`, "info");
    else showToast(`OTP sent to ${form.email.trim()}`, "success");
  };

  const verifyOtp = async () => {
    setVerifyError("");
    const entered = otp.join("");
    if (entered.length < 6) { setVerifyError("Enter all 6 digits"); return; }
    setLoading(true);
    const result = await OTPService.verifyEmail("signup", form.email, entered);
    setLoading(false);
    if (!result.success) { setVerifyError(result.error); return; }
    setVerificationToken(result.token);
    showToast("Email verified", "success");
    setStep(3);
  };

  const handleOtpChange = (i, val) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...otp];
    next[i] = val.slice(-1);
    setOtp(next);
    if (val && i < 5) otpRefs[i + 1].current?.focus();
    if (!val && i > 0) otpRefs[i - 1].current?.focus();
  };

  const submit = async () => {
    if (!form.companyName) { showToast("Company name required", "error"); return; }
    setLoading(true);

    if (oauthSession) {
      if (!form.phone || !form.phone.match(/^\d{10}$/)) {
        showToast("Enter a valid 10-digit mobile number", "error");
        setLoading(false);
        return;
      }
      const result = await Auth.completeOAuthSignup({
        oauth: oauthSession,
        name: form.name || oauthSession.name,
        email: form.email,
        phone: form.phone,
        companyName: form.companyName,
        companyType: form.companyType,
        provider: oauthSession.provider,
      });
      setLoading(false);
      if (result.error) { showToast(result.error, "error"); return; }
      clearOAuthSignupSession();
      setOauthSession(null);
      await KRAYA.welcomeClient(form.phone, form.name).catch(() => {});
      if (result.user && onLogin) {
        showToast(`Welcome, ${result.user.name}!`, "success");
        onLogin(result.user);
        return;
      }
      setStep(5);
      return;
    }

    if (!verificationToken) { showToast("Verify your email first", "error"); setStep(2); setLoading(false); return; }
    const result = await Auth.signUp({
      name: form.name,
      email: form.email.trim().toLowerCase(),
      phone: form.phone,
      password: form.password,
      companyName: form.companyName,
      companyType: form.companyType,
      role: "client",
      verificationToken,
    });
    setLoading(false);
    if (result.error) { showToast(result.error, "error"); return; }
    await KRAYA.welcomeClient(form.phone, form.name).catch(() => {});
    if (result.user && onLogin) {
      showToast(`Welcome, ${result.user.name}!`, "success");
      onLogin(result.user);
      return;
    }
    setStep(5);
  };

  return (
    <div className="auth-wrap auth-wrap--dark">
      <div className="auth-orb" style={{ width: 400, height: 400, top: -100, right: -100, background: "linear-gradient(135deg,#7C3AED,transparent)", opacity: .1 }} />
      <div className="auth-card auth-card--panel">

        {step < 5 && (
          <>
            <button onClick={onBack} style={{ background: "none", border: "none", color: "var(--muted)", cursor: "pointer", fontSize: 13, marginBottom: 20, display: "flex", alignItems: "center", gap: 4 }}>← Back to login</button>
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <AuthLogo size={72} />
            </div>
            <div style={{ fontFamily: "'Fraunces',serif", fontSize: 22, marginBottom: 4, textAlign: "center" }}>Create Account</div>
            <div style={{ fontSize: 11, color: "var(--muted)", letterSpacing: 2, textTransform: "uppercase", marginBottom: 24 }}>Founders Bridge Portal</div>

            <div style={{ display: "flex", gap: 4, marginBottom: 28 }}>
              {STEPS.slice(0, 4).map((s, i) => (
                <div key={s} style={{ flex: 1, height: 4, borderRadius: 4, background: i + 1 <= step ? "var(--blue)" : "var(--border)", transition: ".3s" }} />
              ))}
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 18 }}>Step {step} — {STEPS[step - 1]}</div>
          </>
        )}

        {step === 1 && (
          <div className="form-grid">
            <div className="f-group"><label className="f-label">Full Name <span className="f-req">*</span></label><input className="f-input" placeholder="As per PAN card" value={form.name} onChange={e => set("name", e.target.value)} /></div>
            <div className="f-group"><label className="f-label">Mobile Number <span className="f-req">*</span></label><input className="f-input" type="tel" placeholder="10-digit mobile" value={form.phone} onChange={e => set("phone", e.target.value.replace(/\D/g, ""))} maxLength={10} /></div>
            <div className="f-group"><label className="f-label">Email Address <span className="f-req">*</span></label><input className="f-input" type="email" placeholder="your@email.com" value={form.email} onChange={e => set("email", e.target.value)} /></div>
            <OAuthButtons mode="signup" onError={(msg) => showToast(msg, "error")} disabled={loading} />
            <button
              className="btn btn-primary btn-lg"
              style={{ width: "100%", marginTop: 8 }}
              onClick={() => {
                if (!form.name || !form.phone || !form.email) { showToast("All fields required", "error"); return; }
                if (!form.phone.match(/^\d{10}$/)) { showToast("Enter a valid 10-digit mobile number", "error"); return; }
                if (!form.email.includes("@")) { showToast("Enter a valid email address", "error"); return; }
                setStep(2);
              }}
            >
              Verify email with OTP →
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="form-grid">
            <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 8 }}>
              We&apos;ll email a 6-digit code to <strong>{form.email.trim() || "your address"}</strong> to verify it&apos;s you.
            </div>
            {!otpSent ? (
              <>
                {verifyError && <div style={{ fontSize: 12, color: "var(--red)", padding: "8px 12px", background: "#FFF1F2", borderRadius: 6 }}>{verifyError}</div>}
                <button className="btn btn-primary btn-lg" style={{ width: "100%" }} onClick={sendEmailOtp} disabled={loading}>
                  Send OTP to Email
                </button>
                <button className="btn" style={{ width: "100%" }} onClick={() => { setOtpSent(false); setStep(1); setVerifyError(""); }}>← Back</button>
              </>
            ) : (
              <>
                <div style={{ fontSize: 12, color: "var(--muted)" }}>
                  Enter the code sent to {form.email.trim()}
                </div>
                <div className="otp-grid">
                  {otp.map((d, i) => (
                    <input
                      key={i}
                      ref={otpRefs[i]}
                      className="f-input otp-box"
                      inputMode="numeric"
                      autoComplete={i === 0 ? "one-time-code" : "off"}
                      maxLength={1}
                      value={d}
                      onChange={e => handleOtpChange(i, e.target.value)}
                    />
                  ))}
                </div>
                {verifyError && <div style={{ fontSize: 12, color: "var(--red)" }}>{verifyError}</div>}
                <button className="btn btn-primary btn-lg" style={{ width: "100%" }} onClick={verifyOtp} disabled={loading}>
                  {loading ? "Verifying…" : "Verify & Continue →"}
                </button>
                <button className="btn btn-sm" style={{ width: "100%" }} disabled={countdown > 0 || loading} onClick={sendEmailOtp}>
                  {countdown > 0 ? `Resend in ${countdown}s` : "Resend OTP"}
                </button>
                <button className="btn btn-sm" style={{ width: "100%" }} onClick={() => { setOtpSent(false); setStep(1); setVerifyError(""); setOtp(["", "", "", "", "", ""]); }}>
                  ← Edit email
                </button>
              </>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="form-grid">
            <div className="f-group"><label className="f-label">Set Password <span className="f-req">*</span></label><input className="f-input" type="password" placeholder="Minimum 6 characters" value={form.password} onChange={e => set("password", e.target.value)} /></div>
            <div className="f-group"><label className="f-label">Confirm Password <span className="f-req">*</span></label><input className="f-input" type="password" placeholder="Repeat password" value={form.confirmPassword} onChange={e => set("confirmPassword", e.target.value)} /></div>
            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <button className="btn" style={{ flex: 1 }} onClick={() => setStep(2)}>← Back</button>
              <button className="btn btn-primary" style={{ flex: 2 }} onClick={nextStep}>Next →</button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="form-grid">
            {oauthSession && (
              <div style={{ padding: "10px 14px", background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 8, fontSize: 12, color: "var(--blue)" }}>
                Signed in with Google/Apple. Add your mobile number and company details to finish.
              </div>
            )}
            {oauthSession && (
              <>
                <div className="f-group"><label className="f-label">Full Name</label><input className="f-input" value={form.name} onChange={e => set("name", e.target.value)} /></div>
                <div className="f-group"><label className="f-label">Email</label><input className="f-input" type="email" value={form.email} onChange={e => set("email", e.target.value)} /></div>
                <div className="f-group"><label className="f-label">Mobile Number <span className="f-req">*</span></label><input className="f-input" type="tel" placeholder="10-digit mobile" value={form.phone} onChange={e => set("phone", e.target.value.replace(/\D/g, ""))} maxLength={10} /></div>
              </>
            )}
            <div className="f-group"><label className="f-label">Company / Business Name <span className="f-req">*</span></label><input className="f-input" placeholder="e.g. TechSpark Solutions Pvt Ltd" value={form.companyName} onChange={e => set("companyName", e.target.value)} /></div>
            <div className="f-group">
              <label className="f-label">Business Type</label>
              <select className="f-select" value={form.companyType} onChange={e => set("companyType", e.target.value)}>
                {["Private Limited", "LLP", "Partnership", "Proprietorship", "Individual"].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div style={{ padding: "12px 14px", background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 8, fontSize: 12, color: "var(--blue)", display: "flex", alignItems: "flex-start", gap: 6 }}>
              <Icon name="info" size={14} style={{ marginTop: 1 }} /><span>Our team will review your registration and be in touch within 24 hours to complete your onboarding.</span>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn" style={{ flex: 1 }} onClick={() => oauthSession ? onBack() : setStep(3)}>← Back</button>
              <button className="btn btn-primary" style={{ flex: 2 }} onClick={submit} disabled={loading}>
                {loading ? "Creating account…" : "Create Account →"}
              </button>
            </div>
          </div>
        )}

        {step === 5 && (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}><Icon name="sparkles" size={44} /></div>
            <div style={{ fontFamily: "'Fraunces',serif", fontSize: 24, marginBottom: 8 }}>Account Created!</div>
            <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 8 }}>Welcome to Founders Bridge, <strong>{form.name}</strong>!</div>
            <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 20 }}>Our team will contact you within 24 hours to set up your portal.</div>
            <div style={{ padding: "10px 14px", background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 8, fontSize: 12, color: "var(--green)", marginBottom: 20 }}>
              <InlineIcon name="whatsapp" size={14} gap={6}>A welcome message has been sent to your WhatsApp</InlineIcon>
            </div>
            <button className="btn btn-primary btn-lg" style={{ width: "100%" }} onClick={onBack}>Go to Login →</button>
          </div>
        )}
      </div>
    </div>
  );
}

export { RegistrationPage };
